import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Alert,
  Snackbar,
  InputLabel,
  styled,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format, parseISO, isValid, startOfYear, endOfYear, startOfDay } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { fetchAllActiveUsers, type UserBasicInfo } from '../../services/supabase/user.service';
import { notifyAdminsOfUndertime } from '../../services/supabase/undertime.service';
import {
  type UndertimeType,
  UNDERTIME_TYPE_LABELS,
  calculateUndertimeHours,
  formatTimeString,
  displayTime,
  formatHours,
} from '../../utils/undertimeHours';

// Matches the leave form: flatten on phones so the form isn't a heavy card
// and can't push past the viewport edge.
const FormPaperStyled = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
}));

interface UndertimeApplicationData {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_username?: string | null;
  undertime_type: UndertimeType | string;
  custom_reason?: string | null;
  reason?: string | null;
  request_date: string;
  undertime_date: string;
  time_from?: string | null;
  time_to?: string | null;
  undertime_hours: number;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string | null;
  submitted_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
}

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' =>
  status === 'approved' ? 'success' : status === 'rejected' ? 'error' : status === 'pending' ? 'warning' : 'default';

// ── The logged-in user's own undertime records ─────────────────────────────
const UndertimeHistory: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userProfile } = useAuth();

  const [records, setRecords] = useState<UndertimeApplicationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const userId = userProfile?.id;

  const fetchRecords = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('undertime_applications_view')
        .select('*')
        .eq('employee_id', userId)
        .order('undertime_date', { ascending: false });

      if (error) throw error;
      setRecords((data || []) as UndertimeApplicationData[]);
    } catch (err: any) {
      console.error('Error fetching undertime records:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshKey]);

  const filtered = records.filter((r) => {
    if (filterType !== 'all' && r.undertime_type !== filterType) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const approvedThisYear = useMemo(() => {
    const now = new Date();
    const yearStart = startOfDay(startOfYear(now));
    const yearEnd = startOfDay(endOfYear(now));

    const breakdown: Record<string, number> = {};
    let total = 0;

    records
      .filter((r) => r.status === 'approved')
      .forEach((r) => {
        const d = parseISO(r.undertime_date);
        if (!isValid(d) || d < yearStart || d > yearEnd) return;
        const hours = Number(r.undertime_hours);
        if (!Number.isFinite(hours) || hours <= 0) return;
        total += hours;
        breakdown[r.undertime_type] = (breakdown[r.undertime_type] || 0) + hours;
      });

    const items = Object.entries(breakdown)
      .map(([type, hours]) => ({ type, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours);

    return { total: Math.round(total * 100) / 100, items };
  }, [records]);

  return (
    <Box sx={{ mt: { xs: 3, sm: 4 } }}>
      <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom sx={{ fontWeight: 600 }}>
        Undertime History
      </Typography>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
          Approved undertime (this year)
        </Typography>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.25 }}>
          {formatHours(approvedThisYear.total)}
        </Typography>
        <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {approvedThisYear.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No approved undertime yet for this year.
            </Typography>
          ) : (
            approvedThisYear.items.map((it) => (
              <Chip
                key={it.type}
                variant="outlined"
                color="warning"
                size="small"
                label={`${UNDERTIME_TYPE_LABELS[it.type as UndertimeType] || it.type}: ${formatHours(it.hours)}`}
              />
            ))
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Undertime does not consume leave credits. These hours are logged for payroll deduction only.
        </Typography>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Undertime Type</InputLabel>
            <Select value={filterType} label="Undertime Type" onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="all">All Types</MenuItem>
              {(Object.keys(UNDERTIME_TYPE_LABELS) as UndertimeType[]).map((t) => (
                <MenuItem key={t} value={t}>
                  {UNDERTIME_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ mt: 2, boxShadow: 3, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          <Table
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: isMobile ? 900 : 650 }}
            aria-label="undertime records table"
          >
            <TableHead sx={{ backgroundColor: 'grey.200' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>From</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>To</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Hours</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No undertime records found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {format(parseISO(r.undertime_date), 'MM/dd/yyyy')}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {UNDERTIME_TYPE_LABELS[r.undertime_type as UndertimeType] || r.undertime_type}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{displayTime(r.time_from)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{displayTime(r.time_to)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {formatHours(r.undertime_hours)}
                    </TableCell>
                    <TableCell sx={{ minWidth: isMobile ? 260 : undefined }}>
                      {r.reason || r.custom_reason || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        color={statusColor(r.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── The apply form ─────────────────────────────────────────────────────────
const UndertimeForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userProfile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState<UserBasicInfo[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = userProfile?.role === 'admin';
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [employeeName, setEmployeeName] = useState(userProfile ? userProfile.username : '');

  const [undertimeType, setUndertimeType] = useState<UndertimeType>('early_out');
  const [otherReason, setOtherReason] = useState('');
  const [undertimeDate, setUndertimeDate] = useState<Date | null>(new Date());
  const [timeFrom, setTimeFrom] = useState<Date | null>(null);
  const [timeTo, setTimeTo] = useState<Date | null>(null);
  const [hours, setHours] = useState<string>('0');
  const [hoursManuallySet, setHoursManuallySet] = useState(false);
  const [reason, setReason] = useState('');

  const showCustomReason = undertimeType === 'other';

  const recomputeHours = useCallback(() => {
    setHours(String(calculateUndertimeHours(timeFrom, timeTo)));
  }, [timeFrom, timeTo]);

  useEffect(() => {
    if (hoursManuallySet) return;
    if (!timeFrom || !timeTo) return;
    recomputeHours();
  }, [timeFrom, timeTo, hoursManuallySet, recomputeHours]);

  const userId = userProfile?.id;
  const username = userProfile?.username;

  useEffect(() => {
    if (!userId || !username) return;
    setEmployeeName(username);
    setSelectedUserId(userId);
  }, [userId, username]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const users = await fetchAllActiveUsers();
        setAllUsers(users);
      } catch (err: any) {
        console.error('Error fetching users:', err);
        setErrorMessage(err.message || 'Failed to load users list');
        setErrorOpen(true);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [isAdmin]);

  const handleUserChange = (event: SelectChangeEvent) => {
    const id = event.target.value;
    setSelectedUserId(id);
    const selected = allUsers.find((u) => u.id === id);
    if (selected) {
      setEmployeeName(selected.username);
    } else if (id === userProfile?.id && userProfile) {
      setEmployeeName(userProfile.username);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    setErrorOpen(false);
    setSuccessOpen(false);

    if (!userProfile) {
      setErrorMessage('User profile not found. Please log in again.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }
    if (!undertimeDate || !isValid(undertimeDate)) {
      setErrorMessage('Undertime date is required.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }
    if (timeFrom && timeTo && calculateUndertimeHours(timeFrom, timeTo) === 0) {
      setErrorMessage('Time To must be later than Time From.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }

    const parsedHours = Number(hours);
    if (!Number.isFinite(parsedHours) || parsedHours <= 0) {
      setErrorMessage('Number of undertime hours must be greater than 0.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }
    if (parsedHours > 24) {
      setErrorMessage('Undertime hours cannot exceed 24 for a single day.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }

    const effectiveUserId = isAdmin && selectedUserId ? selectedUserId : userProfile.id;
    const currentEmployeeName =
      (isAdmin && selectedUserId && allUsers.find((u) => u.id === selectedUserId)?.username) ||
      userProfile.username;
    const isAdminSubmittingForOthers = isAdmin && selectedUserId && selectedUserId !== userProfile.id;

    const payload = {
      employee_id: effectiveUserId,
      employee_name: currentEmployeeName,
      undertime_type: undertimeType,
      custom_reason: undertimeType === 'other' ? otherReason : null,
      reason: reason + (isAdminSubmittingForOthers ? ` (Submitted by admin: ${userProfile.username})` : ''),
      request_date: format(new Date(), 'yyyy-MM-dd'),
      undertime_date: format(undertimeDate, 'yyyy-MM-dd'),
      time_from: formatTimeString(timeFrom),
      time_to: formatTimeString(timeTo),
      undertime_hours: Math.round(parsedHours * 100) / 100,
      status: 'pending' as const,
    };

    try {
      const { data, error } = await supabase
        .from('undertime_applications')
        .insert(payload)
        .select();
      if (error) throw error;

      // Approvals happen in the staff app, so this is the only signal the
      // admins get. Never throws — the request is already saved.
      const inserted = data?.[0];
      if (inserted?.id) {
        await notifyAdminsOfUndertime({
          undertimeId: inserted.id,
          employeeName: currentEmployeeName,
          undertimeDate: payload.undertime_date,
          hours: payload.undertime_hours,
          undertimeType: payload.undertime_type,
          submittedByUserId: userProfile.id,
        });
      }

      setSuccessOpen(true);
      setUndertimeType('early_out');
      setOtherReason('');
      setReason('');
      setUndertimeDate(new Date());
      setTimeFrom(null);
      setTimeTo(null);
      setHours('0');
      setHoursManuallySet(false);
      if (userProfile) {
        setSelectedUserId(userProfile.id);
        setEmployeeName(userProfile.username);
      }
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      console.error('Error submitting undertime application:', err);
      setErrorMessage(err.message || 'Failed to submit undertime application.');
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert severity="info" sx={{ mt: { xs: 1, sm: 2 } }}>
        Undertime is logged for approval and payroll deduction only — it is <strong>not</strong> deducted from
        leave credits.
      </Alert>

      <FormPaperStyled>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={isMobile ? 2 : 3}>
            {isAdmin && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="undertime-user-select-label">File Undertime For</InputLabel>
                  <Select
                    labelId="undertime-user-select-label"
                    value={selectedUserId}
                    label="File Undertime For"
                    onChange={handleUserChange}
                    disabled={loadingUsers}
                  >
                    {loadingUsers ? (
                      <MenuItem disabled>
                        <em>Loading users...</em>
                      </MenuItem>
                    ) : allUsers.length === 0 ? (
                      <MenuItem disabled>
                        <em>No users found</em>
                      </MenuItem>
                    ) : (
                      allUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.username} ({user.role})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {selectedUserId && selectedUserId !== userProfile?.id && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    You are filing undertime on behalf of: {employeeName}.
                  </Alert>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Employee Name"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                InputProps={{
                  readOnly: !isAdmin || (selectedUserId ? selectedUserId === userProfile?.id : true),
                }}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" margin="normal">
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Type of Undertime
                </Typography>
                <RadioGroup
                  row={!isMobile}
                  value={undertimeType}
                  onChange={(e) => setUndertimeType(e.target.value as UndertimeType)}
                  sx={
                    isMobile
                      ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }
                      : undefined
                  }
                >
                  {(Object.keys(UNDERTIME_TYPE_LABELS) as UndertimeType[]).map((t) => (
                    <FormControlLabel key={t} value={t} control={<Radio />} label={UNDERTIME_TYPE_LABELS[t]} />
                  ))}
                </RadioGroup>
              </FormControl>
              {showCustomReason && (
                <TextField
                  fullWidth
                  label="Specify Other Type"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  required
                  margin="normal"
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason / Details"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Date of Undertime"
                value={undertimeDate}
                onChange={(newValue) => setUndertimeDate(newValue)}
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    helperText: 'Past dates are allowed for emergency or late-filed undertime.',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TimePicker
                label="Time From"
                value={timeFrom}
                onChange={(newValue) => {
                  setTimeFrom(newValue);
                  setHoursManuallySet(false);
                }}
                sx={{ width: '100%' }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TimePicker
                label="Time To"
                value={timeTo}
                onChange={(newValue) => {
                  setTimeTo(newValue);
                  setHoursManuallySet(false);
                }}
                sx={{ width: '100%' }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Number of undertime hours"
                value={hours}
                onChange={(e) => {
                  setHours(e.target.value);
                  setHoursManuallySet(true);
                }}
                onBlur={() => {
                  const n = Number(hours);
                  if (Number.isFinite(n)) setHours(String(Math.max(0, Math.round(n * 100) / 100)));
                }}
                helperText="Auto-computed from the times above. Editable if the times don't tell the whole story."
                margin="normal"
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={8}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                mt: { xs: 0, sm: 2 },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setHoursManuallySet(false);
                  recomputeHours();
                }}
                disabled={!timeFrom || !timeTo}
              >
                Recalculate hours
              </Button>
            </Grid>

            <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                size="large"
                fullWidth={isMobile}
                sx={{ minHeight: 44 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Undertime'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormPaperStyled>

      <Snackbar
        open={successOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          Undertime application submitted successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <UndertimeHistory refreshKey={refreshKey} />
    </>
  );
};

export default UndertimeForm;
